import React, { useEffect, useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Alert, Empty, Table } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
	useLazyGetChannelsQuery,
	useLazyGetChannelsByServerQuery,
	usePostChannelMutation,
	usePutChannelMutation,
} from "../../../redux/channels/channelAPI";
import { updateFormAction, updateOpenDrawerChannel, updateChannel } from "../../../redux/channels/channelSlice";
import { useLazyGetServersQuery } from "../../../redux/servers/serverAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import validatePermission from "../../../utils/validatePermissions";
// COMPONENTS
import ChannelsTableActions from "./ChannelsTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
import FormularyHeaderInputText from "../../components/formularyHeader/FormularyHeaderInputText";
import FormularyHeaderInputNumber from "../../components/formularyHeader/FormularyHeaderInputNumber";
import FormularyHeaderInputSelectString from "../../components/formularyHeader/FormularyHeaderInputSelectString";
import FormularyHeaderInputSelectKeyValue from "../../components/formularyHeader/FormularyHeaderInputSelectKeyValue";
import FormularyHeaderActionsCreate from "../../components/formularyHeader/FormularyHeaderActionsCreate";
import FormularyHeaderActionsEdit from "../../components/formularyHeader/FormularyHeaderActionsEdit";
import FormularyHeaderInputBoolean from "../../components/formularyHeader/FormularyHeaderInputBoolean";
// UTILS
import {
	validateIpAndPortString,
	getTableRowClass,
	validateDoubleIpAndPortString,
	validateDoubleHostAndPortString,
} from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/EdgeManagement.interface";
import { ChannelInterface, ChannelTypeType } from "../../../interfaces/Channel.interface";
// CONSTANTS
import { BLANK_CHANNEL } from "../../../constants/channels";

function ChannelsTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const { Column, ColumnGroup } = Table;
	const [t] = useTranslation("global");
	const dispatch = useDispatch();
	const { theme } = useSelector((state: RootState) => state.home);
	const location = useLocation();
	const idServer = new URLSearchParams(location.search).get("idServer");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [channels, setChannels] = useState<ChannelInterface[]>([]);
	const [servers, setServers] = useState<ServerInterface[]>([]);
	const [channel, setChannel] = useState<ChannelInterface>({ ...BLANK_CHANNEL });
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 0,
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const {
		channelRefreshTable,
		formAction,
		channel: selectedChannel,
	} = useSelector((state: RootState) => state.channel);
	const { userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetChannelsTable,
		{ data: dataChannels, isLoading: isLoadingChannels, isError: isErrorChannels, isFetching: isFetchingChannels },
	] = useLazyGetChannelsQuery();

	const [
		triggerGetChannelsByServerTable,
		{
			data: dataChannelsByServer,
			isLoading: isLoadingChannelsByServer,
			isError: isErrorChannelsByServer,
			isFetching: isFetchingChannelsByServer,
		},
	] = useLazyGetChannelsByServerQuery();

	const [triggerGetServers, { data: dataGetServers, isLoading: isLoadingGetServers }] = useLazyGetServersQuery();

	const [
		triggerPostChannel,
		{
			isSuccess: isSuccessPostChannel,
			isLoading: isLoadingPostChannel,
			isError: isErrorPostChannel,
			error: errorPostChannel,
			reset: resetPostChannel,
		},
	] = usePostChannelMutation();

	const [
		triggerPutChannel,
		{
			isSuccess: isSuccessPutChannel,
			isLoading: isLoadingPutChannel,
			isError: isErrorPutChannel,
			error: errorPutChannel,
			reset: resetPutChannel,
		},
	] = usePutChannelMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getServers = async () => {
		const token = await GETJwtToken();
		triggerGetServers({
			token,
			page: 1,
			limit: 9999,
		});
	};

	const getData = async (page: number, limit: number) => {
		let searchAndFilterString = "";
		if (userLogged && userLogged.org_id && userLogged.org_id !== globalOrganizationId) {
			searchAndFilterString = searchAndFilterString.concat(`&org_id=${globalOrganizationId}`);
		}
		const token = await GETJwtToken();
		if (idServer && idServer !== "") {
			triggerGetChannelsByServerTable({
				page,
				limit,
				token,
				serverId: idServer,
			});
		} else {
			triggerGetChannelsTable({
				page,
				limit,
				token,
				orgId: userLogged.org.id,
				searchAndFilterString,
			});
		}
	};

	const handleTableChange = (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
		getData(pagination_.current, pagination_.pageSize);
	};

	const refreshTable = async () => {
		setPagination({
			...pagination,
			current: 1,
			pageSize: 10,
		});
		getData(1, 10);
		getServers();
	};

	const onChangeFormValue = (newValue: string | ChannelTypeType | number | null, variableName: string) => {
		const copy: ChannelInterface = structuredClone(channel);
		if (variableName === "server_id" && typeof newValue === "string") {
			copy.server_id = newValue;
		} else if (variableName === "name" && typeof newValue === "string") {
			copy.name = newValue;
		} else if (variableName === "index" && typeof newValue === "string") {
			copy.index = newValue;
		} else if (variableName === "message_type" && typeof newValue === "string") {
			copy.message_type = newValue;
		} else if (variableName === "socket_address" && typeof newValue === "string") {
			copy.socket_address = newValue;
		} else if (variableName === "vendor_socket_address" && typeof newValue === "string") {
			copy.vendor_socket_address = newValue;
		} else if (variableName === "reporting_socket_address" && typeof newValue === "string") {
			copy.reporting_socket_address = newValue;
		} else if (variableName === "connection_type" && typeof newValue === "string") {
			copy.connection_type = newValue;
		} else if (variableName === "type" && typeof newValue === "string") {
			copy.type = newValue;
		} else if (variableName === "heartbeat_frequency" && (typeof newValue === "number" || newValue === null)) {
			copy.heartbeat_frequency = newValue;
		}
		setChannel(copy);
	};

	const onChangeFormValueBoolean = (newValue: boolean, variableName: string) => {
		const copy: ChannelInterface = structuredClone(channel);
		if (variableName === "active") {
			copy.active = newValue;
		}
		setChannel(copy);
	};

	const checkFormIsValid = () => {
		let socketsValid = false;
		if (channel.connection_type === "TCP") {
			const hasSocket = validateIpAndPortString(channel.socket_address || "");
			const hasVendorSocket = validateIpAndPortString(channel.vendor_socket_address || "");
			socketsValid = hasSocket || hasVendorSocket;
		}
		if (channel.connection_type === "UDP") {
			const hasSocket = validateDoubleIpAndPortString(channel.socket_address || "");
			socketsValid = hasSocket;
		}
		if (channel.connection_type === "SERIAL") {
			const hasSocket = validateDoubleHostAndPortString(channel.socket_address || "");
			const hasVendorSocket = validateDoubleHostAndPortString(channel.vendor_socket_address || "");
			socketsValid = hasSocket && hasVendorSocket;
		}
		if (
			channel.index !== "" &&
			channel.name !== "" &&
			socketsValid &&
			validateIpAndPortString(channel.reporting_socket_address || "") &&
			(channel.heartbeat_frequency ? channel.heartbeat_frequency > 0 : true) &&
			channel.server_id !== ""
		) {
			return false;
		}
		return true;
	};

	const postChannel = async () => {
		const token = await GETJwtToken();
		triggerPostChannel({
			token,
			body: {
				active: true,
				banks: [],
				banks2delete: [],
				index: channel.index,
				name: channel.name,
				type: channel.type,
				message_type: channel.message_type,
				server_id: channel.server_id,
				baud_rate: 0,
				port_number: 0,
				socket_address: channel.socket_address,
				vendor_socket_address: channel.vendor_socket_address,
				reporting_socket_address: channel.reporting_socket_address,
				heartbeat_frequency: channel.heartbeat_frequency,
				connection_type: channel.connection_type,
			},
		});
	};

	const putChannel = async () => {
		const token = await GETJwtToken();
		const copy = structuredClone(channel);
		delete copy.banks;
		delete copy.server;
		triggerPutChannel({ token, body: copy, id: channel.id });
	};

	const onClickSave = async () => {
		if (formAction === "create") {
			postChannel();
		} else if (formAction === "edit") {
			putChannel();
		}
	};

	const onClickResetForm = () => {
		resetPostChannel();
		resetPutChannel();
		dispatch(updateFormAction("create"));
		const copy: ChannelInterface = structuredClone(channel);
		copy.name = "";
		copy.vendor_socket_address = "";
		setChannel(copy);
	};

	const generateIncrementableIndex = (total: number) => {
		const defaultSocketAddress: number = 30000 + total + 1;
		const defaultReportingSocketAddress: number = 40000 + total + 1;

		const copy: ChannelInterface = structuredClone(channel);
		copy.index = `${total + 1}`;
		copy.socket_address = `127.0.0.1:${defaultSocketAddress}`;
		copy.reporting_socket_address = `127.0.0.1:${defaultReportingSocketAddress}`;
		if (idServer && idServer !== "") {
			copy.server_id = idServer;
		}
		setChannel(copy);
	};

	const onClickOpenChannelInformation = (record: ChannelInterface) => {
		if (record) {
			dispatch(updateChannel(record));
			dispatch(updateOpenDrawerChannel(true));
		}
	};

	const getInputValidationType = (connectionType: string) => {
		if (connectionType === "TCP") {
			return "ipAndPort";
		}
		if (connectionType === "UDP") {
			return "doubleIpAndPort";
		}
		if (connectionType === "SERIAL") {
			return "stringIpAndPort";
		}
		return "ip";
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		const newData = structuredClone(dataChannels || dataChannelsByServer);
		if (newData && newData.data) {
			setPagination({
				...pagination,
				total: newData.total,
			});
			setChannels(newData.data);
			generateIncrementableIndex(newData.total || 0);
		}
	}, [dataChannels, dataChannelsByServer]);

	useEffect(() => {
		if (globalOrganizationId && globalOrganizationId !== "") {
			getData(pagination.current, pagination.pageSize);
			getServers();
		}
	}, [globalOrganizationId, idServer]);

	useEffect(() => {
		if (channelRefreshTable) {
			refreshTable();
		}
	}, [channelRefreshTable]);

	useEffect(() => {
		if (isErrorChannels || isErrorChannelsByServer) {
			setChannels([]);
		}
	}, [isErrorChannels, isErrorChannelsByServer]);

	useEffect(() => {
		if (dataGetServers && dataGetServers.data) {
			setServers(dataGetServers.data);
		}
	}, [dataGetServers]);

	useEffect(() => {
		if (selectedChannel && selectedChannel.id && selectedChannel.id !== "") {
			setChannel({ ...selectedChannel });
		}
	}, [selectedChannel]);

	useEffect(() => {
		if (isSuccessPostChannel || isSuccessPutChannel || isErrorPostChannel || isErrorPutChannel) {
			onClickResetForm();
		}
	}, [isSuccessPostChannel, isSuccessPutChannel, isErrorPostChannel, isErrorPutChannel]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='mt-3'>
			<>
				{validatePermission("channels-view", permissions) ? (
					<>
						{!isErrorChannels ? (
							<Table
								rowClassName={(record, index) => getTableRowClass(index, theme)}
								className='my-custom-table '
								dataSource={channels}
								loading={
									isLoadingChannels ||
									isFetchingChannels ||
									isLoadingChannelsByServer ||
									isFetchingChannelsByServer ||
									isLoadingPostChannel ||
									isLoadingPutChannel ||
									isLoadingGetServers
								}
								rowKey={(record) => record.id}
								size='small'
								pagination={pagination}
								onChange={handleTableChange}
								scroll={{ x: 900 }}
							>
								{/** SERVER */}
								{FormularyHeaderInputSelectKeyValue({
									columnName: t("general.server"),
									inputPlaceholder: t("general.server"),
									formAction,
									value: channel.server_id,
									onChangeFormValue,
									variableName: "server_id",
									itemId: channel.id,
									values: servers,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** NAME */}
								{FormularyHeaderInputText({
									columnName: t("general.name"),
									inputPlaceholder: t("general.name"),
									formAction,
									value: channel.name,
									onChangeFormValue,
									variableName: "name",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** TYPE */}
								{FormularyHeaderInputSelectString({
									columnName: t("general.type"),
									inputPlaceholder: t("general.type"),
									formAction,
									value: channel.type,
									onChangeFormValue,
									variableName: "type",
									itemId: channel.id,
									values: ["ADAPTER"],
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** INDEX */}
								{FormularyHeaderInputText({
									columnName: "Index",
									inputPlaceholder: "Index",
									formAction,
									value: channel.index,
									onChangeFormValue,
									variableName: "index",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** MESSAGE TYPE */}
								{FormularyHeaderInputSelectString({
									columnName: t("general.messageType"),
									inputPlaceholder: t("general.messageType"),
									formAction,
									value: channel.message_type,
									onChangeFormValue,
									variableName: "message_type",
									itemId: channel.id,
									values: ["LN2-PRTCL"],
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** CONNECTION TYPE */}
								{FormularyHeaderInputSelectString({
									columnName: t("general.connectionType"),
									inputPlaceholder: t("general.connectionType"),
									formAction,
									value: channel.connection_type,
									onChangeFormValue,
									variableName: "connection_type",
									itemId: channel.id,
									values: ["TCP", "SERIAL", "UDP"],
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** SOCKET ADDRESS */}
								{FormularyHeaderInputText({
									columnName: t("general.socketAddress"),
									inputPlaceholder: t("general.socketAddress"),
									formAction,
									value: channel.socket_address,
									onChangeFormValue,
									variableName: "socket_address",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
									inputValidationType: getInputValidationType(channel.connection_type),
								})}
								{/** VENDOR SOCKET ADDRESS */}
								{FormularyHeaderInputText({
									columnName: t("general.vendorSocketAddress"),
									inputPlaceholder: t("general.vendorSocketAddress"),
									formAction,
									value: channel.vendor_socket_address,
									onChangeFormValue,
									variableName: "vendor_socket_address",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
									inputValidationType: getInputValidationType(channel.connection_type),
								})}
								{/** REPORTING SOCKET ADDRESS */}
								{FormularyHeaderInputText({
									columnName: t("general.reportingSocketAddress"),
									inputPlaceholder: t("general.reportingSocketAddress"),
									formAction,
									value: channel.reporting_socket_address,
									onChangeFormValue,
									variableName: "reporting_socket_address",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
									inputValidationType: "ipAndPort",
								})}
								{/** HEARTBEAT FREQUENCY */}
								{FormularyHeaderInputNumber({
									columnName: "Heartbeat",
									inputPlaceholder: "Heartbeat",
									formAction,
									value: channel.heartbeat_frequency,
									onChangeFormValue,
									variableName: "heartbeat_frequency",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** STATUS */}
								{FormularyHeaderInputBoolean({
									columnName: "Status",
									inputPlaceholder: "Status",
									formAction,
									value: channel.active,
									onChangeFormValueBoolean,
									variableName: "active",
									itemId: channel.id,
									onClickCellName: onClickOpenChannelInformation,
									enabledCellClick: true,
								})}
								{/** ACTIONS */}
								<ColumnGroup align='center' title={t("edgeManagement.banks.actions")}>
									<Column
										align='center'
										title={
											<FormularyHeaderActionsCreate
												formAction={formAction}
												formIsValid={checkFormIsValid()}
												onClickSave={onClickSave}
												onClickResetForm={onClickResetForm}
											/>
										}
										key='action'
										render={(_: any, record: ChannelInterface) => (
											<div>
												{formAction === "edit" && record.id === channel.id ? (
													<FormularyHeaderActionsEdit
														formIsValid={checkFormIsValid()}
														onClickSave={onClickSave}
														onClickResetForm={onClickResetForm}
													/>
												) : (
													<ChannelsTableActions selectedItem={record} />
												)}
											</div>
										)}
									/>
								</ColumnGroup>
							</Table>
						) : (
							<Alert message='errors' description='errors' type='error' showIcon className='mt-3' />
						)}
					</>
				) : (
					<>
						<div className='generalStyles__noAccessToListTable'>
							<Empty description={t("general.listNotAvailable")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
						</div>
					</>
				)}
			</>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostChannel}
				isSuccess={isSuccessPostChannel}
				requestType='POST'
				error={errorPostChannel}
				name='Channel'
			/>
			<GlobalAlert2
				isError={isErrorPutChannel}
				isSuccess={isSuccessPutChannel}
				requestType='PUT'
				error={errorPutChannel}
				name='Channel'
			/>
		</div>
	);
}

export default ChannelsTable;
